import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ArrowLeft, Printer } from "lucide-react";
import { useEffect, useId, useRef } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { ReceiptViewModel } from "../../../../shared/contracts.js";
import { request } from "../../../api/http.js";
import { AsyncPanel } from "../../../ui/AsyncPanel.js";
import { Button, buttonAttributes } from "../../../ui/Button.js";
import { roLabel, technicianLabel, vinEndingLabel } from "../presentation.js";

/**
 * The printable order receipt (spec §15).
 *
 * `/cases/:caseId/print` renders the server's receipt projection —
 * `GET /api/cases/:caseId/receipt`, Parts only — as one printable article:
 * a heading with the case identifier, a definition list of the case and
 * vehicle facts, one table of part lines, the optional order note, and a
 * footer naming the creator and the print instant. The page shows exactly
 * what the server sent; it never reads the case detail, so nothing the
 * receipt excludes (verification, exceptions, comments, email state) can
 * find its way onto paper.
 *
 * Printing is the browser's own dialog (`window.print`), which also offers
 * Save as PDF. Parts presses *Print receipt*, or arrives from *Create and
 * print* with `?autoprint=1`, which prints exactly once after the receipt
 * has rendered — never on an error state, never again on a refetch. The
 * page performs no mutation, so closing the dialog changes nothing about
 * the order. The print controls sit outside the printable container and
 * are `screen-only`; `print.css` hides them and lays the article out on
 * US Letter in black on white.
 */

/** The route pattern the router registers for the print page. */
export const RECEIPT_ROUTE_PATH = "/cases/:caseId/print";

/** Also answered, so a link written as ".../receipt" lands on the same page. */
export const RECEIPT_ALIAS_ROUTE_PATH = "/cases/:caseId/receipt";

/** The query parameter that prints once the receipt has rendered. */
export const AUTOPRINT_PARAM = "autoprint";

/**
 * `/cases/:caseId/print`, with `?autoprint=1` when the page should print as
 * soon as the receipt is on screen (the *Create and print* destination).
 */
export function receiptPath(caseId: string, options: { autoprint?: boolean } = {}): string {
  const base = `/cases/${encodeURIComponent(caseId)}/print`;
  return options.autoprint ? `${base}?${AUTOPRINT_PARAM}=1` : base;
}

/** `GET /api/cases/:caseId/receipt` — the approved projection, nothing more. */
export function receiptApiPath(caseId: string): string {
  return `/api/cases/${encodeURIComponent(caseId)}/receipt`;
}

/** Under the `["cases", …]` root so any Parts action marks a mounted receipt stale too. */
export const receiptQueryKey = (caseId: string) => ["cases", "receipt", caseId] as const;

export function fetchReceipt(caseId: string): Promise<ReceiptViewModel> {
  return request<ReceiptViewModel>(receiptApiPath(caseId));
}

export function useReceipt(caseId: string): UseQueryResult<ReceiptViewModel> {
  return useQuery({
    queryKey: receiptQueryKey(caseId),
    queryFn: () => fetchReceipt(caseId),
    enabled: caseId.length > 0,
  });
}

const instantFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Chicago",
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

/** "Sep 16, 2026, 1:45 PM" in the dealership's timezone; an unparseable value is shown as-is. */
export function formatReceiptInstant(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : instantFormatter.format(date);
}

/** Where *Back to case* goes: the Parts workspace with this case open, on the list that holds every case. */
export function caseWorkspacePath(caseId: string): string {
  return `/parts?view=all&case=${encodeURIComponent(caseId)}`;
}

export function OrderReceiptPage() {
  const { caseId = "" } = useParams<{ caseId: string }>();
  const [searchParams] = useSearchParams();
  const autoprint = searchParams.get(AUTOPRINT_PARAM) === "1";
  const query = useReceipt(caseId);
  const receipt = query.data;

  // Auto-print exactly once per case, and only once the receipt is rendered:
  // the effect runs after the article is in the DOM, the ref survives React's
  // development double-invocation and every later refetch, and an error state
  // never reaches it because `receipt` is undefined there.
  const printedForCase = useRef<string | null>(null);
  useEffect(() => {
    if (!autoprint || !receipt || printedForCase.current === caseId) return;
    printedForCase.current = caseId;
    window.print();
  }, [autoprint, receipt, caseId]);

  // The print dialog's default file name is the document title.
  useEffect(() => {
    if (!receipt) return;
    const previous = document.title;
    document.title = `Order receipt ${receipt.caseNumber}`;
    return () => {
      document.title = previous;
    };
  }, [receipt]);

  return (
    <main className="receipt-page">
      <div className="receipt-toolbar screen-only">
        <Link to={caseWorkspacePath(caseId)} {...buttonAttributes({ variant: "quiet" })}>
          <span className="button__icon" aria-hidden="true">
            <ArrowLeft />
          </span>
          <span className="button__label">Back to case</span>
        </Link>
        <Button
          variant="primary"
          icon={<Printer />}
          onClick={() => window.print()}
          disabled={!receipt}
        >
          Print receipt
        </Button>
      </div>

      <AsyncPanel
        isLoading={query.isPending}
        error={query.isError && !receipt ? query.error : undefined}
        refreshError={query.isError && receipt ? query.error : undefined}
        refreshTitle="Couldn't refresh this receipt."
        onRetry={() => {
          void query.refetch();
        }}
        loadingLabel="Loading receipt…"
        className="receipt-panel"
      >
        {receipt ? <Receipt receipt={receipt} /> : null}
      </AsyncPanel>
    </main>
  );
}

function Receipt({ receipt }: { receipt: ReceiptViewModel }) {
  const titleId = useId();
  const detailsId = useId();
  const noteId = useId();

  return (
    <article className="receipt" aria-labelledby={titleId}>
      <header className="receipt__header">
        <div>
          <p className="receipt__kicker">Parts order</p>
          <h1 id={titleId} className="receipt__title">
            Order receipt
          </h1>
        </div>
        <p className="receipt__case-number">{receipt.caseNumber}</p>
      </header>

      {/* The first thing anyone at the counter sees: directly under the header
          rule, in the largest type on the page. Only when ticked — an order
          for a car that is not here prints exactly as it always has. */}
      {receipt.carInShop ? (
        <p className="receipt__car-in-shop" role="note">
          Car in shop
        </p>
      ) : null}

      <section className="receipt__details" aria-labelledby={detailsId}>
        <h2 id={detailsId} className="visually-hidden">
          Order details
        </h2>
        <dl className="receipt__facts">
          <div className="receipt__fact">
            <dt>Created</dt>
            <dd>
              <time dateTime={receipt.createdAt}>{formatReceiptInstant(receipt.createdAt)}</time>
            </dd>
          </div>
          <div className="receipt__fact">
            <dt>Vehicle</dt>
            <dd>{receipt.vehicleLabel}</dd>
          </div>
          <div className="receipt__fact">
            <dt>RO</dt>
            {/* The same "Pre-RO" wording the workspace rows and inspector use. */}
            <dd>{receipt.roNumber ?? roLabel(null)}</dd>
          </div>
          <div className="receipt__fact">
            <dt>Customer</dt>
            <dd>{receipt.customerName}</dd>
          </div>
          <div className="receipt__fact">
            <dt>VIN ending</dt>
            {/* "—" and "Unassigned" for the two optional people/vehicle facts, as the workspace shows them. */}
            <dd className="receipt__mono">{vinEndingLabel(receipt.vinEnding)}</dd>
          </div>
          <div className="receipt__fact">
            <dt>Advisor</dt>
            <dd>{receipt.advisorName}</dd>
          </div>
          <div className="receipt__fact">
            <dt>Technician</dt>
            <dd>{technicianLabel(receipt.technicianName)}</dd>
          </div>
        </dl>
      </section>

      <table className="receipt__lines">
        <caption className="receipt__caption">Parts ordered</caption>
        <thead>
          <tr>
            <th scope="col">Part number</th>
            <th scope="col">Description</th>
            <th scope="col" className="receipt__numeric">
              Quantity
            </th>
            <th scope="col">ETA</th>
          </tr>
        </thead>
        <tbody>
          {receipt.lines.map((line, index) => (
            <tr key={`${index}-${line.partNumber}`}>
              <td className="receipt__mono">{line.partNumber}</td>
              <td>{line.description}</td>
              <td className="receipt__numeric">{line.quantityOrdered}</td>
              <td>{line.etaLabel}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {receipt.orderNote ? (
        <section className="receipt__note" aria-labelledby={noteId}>
          <h2 id={noteId} className="receipt__section-title">
            Order note
          </h2>
          <p className="receipt__note-body">{receipt.orderNote}</p>
        </section>
      ) : null}

      <footer className="receipt__footer">
        <p>Created by {receipt.creatorName}</p>
        <p>
          Printed <time dateTime={receipt.printedAt}>{formatReceiptInstant(receipt.printedAt)}</time>
        </p>
      </footer>
    </article>
  );
}
